import type IViewStatePublisher from "./view-state-publisher.interface";
import type { PublishViewStateResult } from "./view-state.contract";
import { useState, useSyncExternalStore } from "react";

import { useRouter, useRouterState } from "@tanstack/react-router";
import {
    type SeekerScreenContext,
    seekerScreenContextSchema,
    seekerScreenDogSchema,
    seekerScreenProviderSchema,
} from "@wags/agent-contracts/kits-seeker-screen.contract.ts";

import {
    decodeQuery,
    mergeViewState,
    serializeForChangeDetection,
    shouldPublish,
    stableStringify,
    type ViewState,
} from "./view-state.ts";

export const VIEWSTATE_DEBOUNCE_MS = 400;

const EMPTY_SNAPSHOT: ViewState = {
    page: "",
    query: {},
    slices: {},
    updatedAt: 0,
};

let page = "";
let query: Record<string, string> = {};
let slices: ViewState["slices"] = {};
let snapshot: ViewState = EMPTY_SNAPSHOT;

let publisher: IViewStatePublisher | null = null;
let lastPublished: string | null = null;
let debounceTimer: ReturnType<typeof setTimeout> | null = null;
let publishInFlight = false;
let publishFollowUpNeeded = false;

function rebuildSnapshot(): void {
    snapshot = mergeViewState({ page, query }, { ...slices }, Date.now());
}

function doPublish(): void {
    if (!publisher) return;
    if (debounceTimer) {
        clearTimeout(debounceTimer);
        debounceTimer = null;
    }
    if (publishInFlight) {
        publishFollowUpNeeded = true;
        return;
    }
    const next = serializeForChangeDetection(snapshot);
    if (!shouldPublish(lastPublished, next)) return;
    const prev = lastPublished;
    lastPublished = next;
    const rollbackIfUnwritten = (response: PublishViewStateResult): void => {
        if (
            response.status !== "written" &&
            response.status !== "oversized" &&
            lastPublished === next
        ) {
            lastPublished = prev;
            console.warn(
                "[kits] view-state publish was not written; will retry",
            );
        }
    };
    const finishPublish = (): void => {
        publishInFlight = false;
        if (publishFollowUpNeeded) {
            publishFollowUpNeeded = false;
            doPublish();
        }
    };

    publishInFlight = true;
    let result: ReturnType<IViewStatePublisher>;
    try {
        result = publisher(snapshot);
    } catch (error) {
        publishInFlight = false;
        if (lastPublished === next) lastPublished = prev;
        console.warn("[kits] view-state publish failed; will retry", error);
        if (publishFollowUpNeeded) {
            publishFollowUpNeeded = false;
            doPublish();
        }
        return;
    }
    if ("then" in result) {
        result.then(
            (response) => {
                rollbackIfUnwritten(response);
                finishPublish();
            },
            (error) => {
                if (lastPublished === next) lastPublished = prev;
                console.warn(
                    "[kits] view-state publish failed; will retry",
                    error,
                );
                finishPublish();
            },
        );
    } else {
        rollbackIfUnwritten(result);
        finishPublish();
    }
}

function schedulePublish(leadingEdge: boolean): void {
    if (leadingEdge) {
        doPublish();
        return;
    }
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
        debounceTimer = null;
        doPublish();
    }, VIEWSTATE_DEBOUNCE_MS);
}

export function setPageAndQuery(
    nextPage: string,
    nextQuery: Record<string, string>,
): void {
    const clampedPage =
        nextPage.length > 256 ? nextPage.slice(0, 256) : nextPage;
    const pageChanged = clampedPage !== page;
    page = clampedPage;
    query = nextQuery;
    rebuildSnapshot();
    schedulePublish(pageChanged);
}

export function setViewSlice(
    namespace: string,
    slice: ViewState["slices"][string],
    leadingEdge = false,
): void {
    if (
        namespace in slices &&
        stableStringify(slices[namespace]) === stableStringify(slice)
    )
        return;
    slices = { ...slices, [namespace]: slice };
    rebuildSnapshot();
    schedulePublish(leadingEdge);
}

export function clearViewSlice(namespace: string): void {
    if (!(namespace in slices)) return;
    const { [namespace]: _dropped, ...rest } = slices;
    slices = rest;
    rebuildSnapshot();
    schedulePublish(false);
}

export function flushViewState(): void {
    doPublish();
}

export function readSeekerScreenContext(): SeekerScreenContext | undefined {
    if (!page) return undefined;
    const boundedQuery = Object.fromEntries(
        Object.entries(query).filter(
            ([key, value]) => key.length <= 128 && value.length <= 512,
        ),
    );
    const dog = seekerScreenDogSchema.safeParse(slices.dog);
    const provider = seekerScreenProviderSchema.safeParse(slices.provider);
    const parsed = seekerScreenContextSchema.safeParse({
        page,
        ...(Object.keys(boundedQuery).length > 0
            ? { query: boundedQuery }
            : {}),
        ...(dog.success ? { dog: dog.data } : {}),
        ...(provider.success ? { provider: provider.data } : {}),
    });
    return parsed.success ? parsed.data : undefined;
}

export function republishViewState(): void {
    lastPublished = null;
    doPublish();
}

export function configureViewStatePublisher(
    fn: IViewStatePublisher | null,
): void {
    publisher = fn;
}

export function useKitsViewStateSync(): void {
    const router = useRouter();
    const pathname = useRouterState({ select: (s) => s.location.pathname });
    const search = useRouterState({ select: (s) => s.location.search });
    const decoded = decodeQuery(search ?? {});
    const key = stableStringify(decoded);
    useSyncExternalStore(
        () => {
            setPageAndQuery(pathname, decoded);
            return router.subscribe("onResolved", (event) => {
                setPageAndQuery(
                    event.toLocation.pathname,
                    decodeQuery(event.toLocation.search),
                );
            });
        },
        () => key,
        () => key,
    );
}

export function usePublishViewState(
    namespace: string,
    slice: ViewState["slices"][string],
    options?: { leadingEdge?: boolean },
): {
    ownerRef: (node: HTMLSpanElement | null) => (() => void) | undefined;
    updateRef: (node: HTMLSpanElement | null) => void;
} {
    const serialized = stableStringify(slice);
    const leadingEdge = options?.leadingEdge ?? false;
    const [publication] = useState(() => ({
        leadingEdge,
        mounted: false,
        namespace,
        serialized,
        slice,
    }));
    const [ownerRef] = useState(() => (node: HTMLSpanElement | null) => {
        if (!node) return;
        publication.mounted = true;
        setViewSlice(
            publication.namespace,
            publication.slice,
            publication.leadingEdge,
        );
        return () => {
            publication.mounted = false;
            clearViewSlice(publication.namespace);
        };
    });
    const updateRef = (node: HTMLSpanElement | null) => {
        if (!node) return;
        const previousNamespace = publication.namespace;
        const unchanged =
            previousNamespace === namespace &&
            publication.serialized === serialized &&
            publication.leadingEdge === leadingEdge;
        publication.leadingEdge = leadingEdge;
        publication.namespace = namespace;
        publication.serialized = serialized;
        publication.slice = slice;
        if (!publication.mounted || unchanged) return;
        if (previousNamespace !== namespace) clearViewSlice(previousNamespace);
        setViewSlice(namespace, slice, leadingEdge);
    };
    return { ownerRef, updateRef };
}
